import { useEffect, useCallback, useRef } from "react";

interface GoogleMapsLoaderProps {
  apiKey: string;
  onLoad: () => void;
  onError: (error: string) => void;
}

// Function to clean up any duplicate Google Maps script tags
const cleanupDuplicateScripts = () => {
  const scripts = document.querySelectorAll('script[src*="maps.googleapis.com"]');
  if (scripts.length > 1) {
    // Keep only the first script, remove the rest
    for (let i = 1; i < scripts.length; i++) {
      scripts[i].remove();
    }
  }
}

// Global state to track loading status across all component instances
let isGoogleMapsLoaded = false;
let isGoogleMapsLoading = false;
let loadedCallbacks: (() => void)[] = [];
let errorCallbacks: ((error: string) => void)[] = [];

export const useGoogleMapsLoader = ({
  apiKey,
  onLoad,
  onError,
}: GoogleMapsLoaderProps) => {
  const hasCalledOnLoad = useRef(false);
  const hasCalledOnError = useRef(false);

  const loadGoogleMaps = useCallback(() => {
    // Clean up any duplicate scripts from previous renders
    cleanupDuplicateScripts();
    
    if (!apiKey) {
      if (!hasCalledOnError.current) {
        hasCalledOnError.current = true;
        onError(
          "Google Maps API key is not configured. Please add REACT_APP_GOOGLE_MAPS_API_KEY to your .env file."
        );
      }
      return;
    }

    // If already loaded, immediately call onLoad
    if (isGoogleMapsLoaded && window.google && window.google.maps && window.google.maps.ControlPosition) {
      if (!hasCalledOnLoad.current) {
        hasCalledOnLoad.current = true;
        onLoad();
      }
      return;
    }

    // Add callbacks to queues
    if (!loadedCallbacks.includes(onLoad)) {
      loadedCallbacks.push(onLoad);
    }
    if (!errorCallbacks.includes(onError)) {
      errorCallbacks.push(onError);
    }

    // If currently loading, just wait
    if (isGoogleMapsLoading) {
      return;
    }

    // Check if Google Maps is already available in window
    if (window.google && window.google.maps && window.google.maps.ControlPosition) {
      isGoogleMapsLoaded = true;
      // Call all pending callbacks
      loadedCallbacks.forEach(callback => callback());
      loadedCallbacks = [];
      return;
    }

    // Check if script is already in the DOM
    const existingScript = document.querySelector('script[src*="maps.googleapis.com"]');
    if (existingScript) {
      // Script exists, wait for it to load
      if (!isGoogleMapsLoading) {
        isGoogleMapsLoading = true;
        existingScript.addEventListener('load', () => {
          // Double-check that Google Maps API is fully loaded
          if (window.google && window.google.maps && window.google.maps.ControlPosition) {
            isGoogleMapsLoaded = true;
            isGoogleMapsLoading = false;
            loadedCallbacks.forEach(callback => callback());
            loadedCallbacks = [];
          } else {
            // Wait a bit more and try again
            setTimeout(() => {
              if (window.google && window.google.maps && window.google.maps.ControlPosition) {
                isGoogleMapsLoaded = true;
                isGoogleMapsLoading = false;
                loadedCallbacks.forEach(callback => callback());
                loadedCallbacks = [];
              } else {
                isGoogleMapsLoading = false;
                const errorMessage = "Google Maps API loaded but not fully initialized";
                errorCallbacks.forEach(callback => callback(errorMessage));
                errorCallbacks = [];
              }
            }, 100);
          }
        });
        existingScript.addEventListener('error', () => {
          isGoogleMapsLoading = false;
          const errorMessage = "Failed to load Google Maps API";
          errorCallbacks.forEach(callback => callback(errorMessage));
          errorCallbacks = [];
        });
      }
      return;
    }

    // Start loading
    isGoogleMapsLoading = true;
    
    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places&loading=async`;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      // Double-check that Google Maps API is fully loaded
      if (window.google && window.google.maps && window.google.maps.ControlPosition) {
        isGoogleMapsLoaded = true;
        isGoogleMapsLoading = false;
        // Call all pending callbacks
        loadedCallbacks.forEach(callback => callback());
        loadedCallbacks = [];
      } else {
        // Wait a bit more and try again
        setTimeout(() => {
          if (window.google && window.google.maps && window.google.maps.ControlPosition) {
            isGoogleMapsLoaded = true;
            isGoogleMapsLoading = false;
            loadedCallbacks.forEach(callback => callback());
            loadedCallbacks = [];
          } else {
            isGoogleMapsLoading = false;
            const errorMessage = "Google Maps API loaded but not fully initialized";
            errorCallbacks.forEach(callback => callback(errorMessage));
            errorCallbacks = [];
          }
        }, 500);
      }
    };
    script.onerror = () => {
      isGoogleMapsLoading = false;
      const errorMessage = "Failed to load Google Maps API. Please check your API key and network connection.";
      errorCallbacks.forEach(callback => callback(errorMessage));
      errorCallbacks = [];
    };
    document.head.appendChild(script);
  }, [apiKey, onLoad, onError]);

  useEffect(() => {
    loadGoogleMaps();
    
    // Cleanup function to remove callbacks when component unmounts
    return () => {
      loadedCallbacks = loadedCallbacks.filter(callback => callback !== onLoad);
      errorCallbacks = errorCallbacks.filter(callback => callback !== onError);
    };
  }, [loadGoogleMaps, onLoad, onError]);
};
